import React, { useEffect, useState, useMemo, useRef } from 'react'
import WrappedVideoBackground from '../Components/WrappedVideoBackground'
import WrappedImageBackground from '../Components/WrappedImageBackground'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions } from 'contexts/Wrapped'
import { VIDEO_FALLBACK_TIMEOUT_MS } from '../config/slides'
import type { SlideConfig, SlideId } from '../types/wrapped'

interface UseVideoBackgroundWithFallbackOptions {
  /** The slide configuration containing video and fallback image info */
  slideConfig: SlideConfig | undefined
  /** The slide ID to check if this is the current slide */
  slideId: SlideId
}

interface UseVideoBackgroundWithFallbackResult {
  /** The background element to render (video or image) */
  background: React.ReactNode | undefined
  /** Whether the background is ready and content can animate */
  isBackgroundReady: boolean
  /** Whether this is the current slide */
  isCurrentSlide: boolean
}

/**
 * Hook that manages video background with image fallback.
 * If the video doesn't load within VIDEO_FALLBACK_TIMEOUT_MS (2 seconds),
 * it falls back to displaying an image background instead.
 */
function useVideoBackgroundWithFallback({
  slideConfig,
  slideId,
}: UseVideoBackgroundWithFallbackOptions): UseVideoBackgroundWithFallbackResult {
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === slideId)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const dispatch = useWrappedStoryDispatch()

  // Track if we've fallen back to image for this slide session
  const [useFallbackImage, setUseFallbackImage] = useState(false)
  // Track if this slide has ever been active (to know when to start timeout)
  const hasBeenActiveRef = useRef(false)
  // Track if video loaded successfully before timeout
  const videoLoadedRef = useRef(false)

  const video = slideConfig?.video
  const fallbackImageId = slideConfig?.fallbackImageId

  // Reset fallback state when slide changes away from this slide
  useEffect(() => {
    if (!isCurrentSlide) {
      // Only reset if we're no longer the current slide
      hasBeenActiveRef.current = false
      videoLoadedRef.current = false
      setUseFallbackImage(false)
    }
  }, [isCurrentSlide])

  // Handle video ready state - mark as loaded if video becomes ready
  useEffect(() => {
    if (isCurrentSlide && isVideoReady && video) {
      videoLoadedRef.current = true
    }
  }, [isCurrentSlide, isVideoReady, video])

  // Start fallback timeout when slide becomes active
  useEffect(() => {
    // Only run if:
    // - This is the current slide
    // - We have a video to show
    // - We have a fallback image available
    // - We haven't already fallen back
    // - Video hasn't loaded yet
    if (!isCurrentSlide || !video || !fallbackImageId || useFallbackImage || videoLoadedRef.current) {
      return
    }

    // Mark that this slide has been activated
    hasBeenActiveRef.current = true

    const timeoutId = setTimeout(() => {
      // If video still hasn't loaded after timeout, fall back to image
      if (!videoLoadedRef.current) {
        setUseFallbackImage(true)
        // Mark background as ready so content can animate
        dispatch({ type: WrappedStoryActions.setVideoReady, isVideoReady: true })
      }
    }, VIDEO_FALLBACK_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [isCurrentSlide, video, fallbackImageId, useFallbackImage, dispatch])

  // Determine if background is ready for content to animate
  const isBackgroundReady = useMemo(() => {
    // No video config means we're ready immediately
    if (!video) return true
    // If using fallback image, we're ready
    if (useFallbackImage) return true
    // Otherwise, check if video is ready
    return isVideoReady
  }, [video, useFallbackImage, isVideoReady])

  // Create the background element
  const background = useMemo(() => {
    if (!video) return undefined

    if (useFallbackImage && fallbackImageId) {
      return (
        <WrappedImageBackground
          imageId={fallbackImageId}
          kenBurnsVariant="zoom"
          isActive={isCurrentSlide && !isPaused}
        />
      )
    }

    return <WrappedVideoBackground video={video} isActive={isCurrentSlide} />
  }, [video, useFallbackImage, fallbackImageId, isCurrentSlide, isPaused])

  return {
    background,
    isBackgroundReady,
    isCurrentSlide,
  }
}

export default useVideoBackgroundWithFallback
